import React, { useMemo } from 'react';
import {
	activityNeedsCustomText,
	COMMUNITY_CHALLENGE_MODES,
	COMMUNITY_MECHANICS,
	findChallengeActivity,
	findCommunityTemplate,
	findMeasurementUnit,
	unitNeedsCustomText
} from '../../constants/communityChallengeTemplates';
import { challengeSummaryPanelClass, hintClass, sectionLabelClass } from './challengeWireFormStyles';
import { buildChallengeScheduleLine, challengeFirstName } from '../../utils/challengeSummaryDisplay';

function activityLabel(activityId, customActivityText) {
	if (!activityId) return null;
	if (activityNeedsCustomText(activityId)) {
		return customActivityText?.trim() || null;
	}
	return findChallengeActivity(activityId)?.label || null;
}

function unitLabel(unitId, customUnitText) {
	if (!unitId) return null;
	if (unitNeedsCustomText(unitId)) {
		return customUnitText?.trim() || null;
	}
	return findMeasurementUnit(unitId)?.label || null;
}

function mechanicLabel(mechanic) {
	return COMMUNITY_MECHANICS.find((m) => m.id === mechanic)?.label || null;
}

function buildAudienceLine(accounts, audienceMode, invitedUserIds) {
	if (!accounts.length) return null;

	if (audienceMode === 'full_network') {
		const profileCount = accounts.reduce((total, account) => total + (account.profile_count || 0), 0);
		return `Visible to ${profileCount} profiles across ${accounts.length} accounts.`;
	}

	const invitedSet = new Set(invitedUserIds);
	const selectedAccounts = accounts.filter((account) => invitedSet.has(account.user_id));
	const profileCount = selectedAccounts.reduce(
		(total, account) => total + (account.profile_count || 0),
		0
	);

	return `Inviting ${profileCount} profiles across ${selectedAccounts.length} accounts.`;
}

export default function CommunityChallengePostSummary({
	mode,
	creatorName,
	title,
	startsOn,
	endsOn,
	description,
	activityId,
	customActivityText,
	mechanic,
	unitId,
	customUnitText,
	featuredKey,
	templateCatalog = [],
	accounts = [],
	audienceMode,
	invitedUserIds = []
}) {
	const isCustomMode = mode === COMMUNITY_CHALLENGE_MODES.CUSTOM;
	const challengeTitle = title?.trim();
	const creator = challengeFirstName(creatorName) || creatorName;
	const scheduleLine = buildChallengeScheduleLine(startsOn, endsOn);
	const descriptionText = description?.trim();
	const audienceLine = buildAudienceLine(accounts, audienceMode, invitedUserIds);

	const customDetails = useMemo(() => {
		if (!isCustomMode) return null;

		const activity = activityLabel(activityId, customActivityText);
		const win = mechanicLabel(mechanic);
		const unit = unitLabel(unitId, customUnitText);
		const parts = [activity, win, unit].filter(Boolean);

		return parts.length ? parts.join(' · ') : null;
	}, [isCustomMode, activityId, customActivityText, mechanic, unitId, customUnitText]);

	const featuredDescription = !isCustomMode
		? findCommunityTemplate(featuredKey, templateCatalog)?.featured_description
		: null;

	const hasRequiredFields =
		Boolean(creator && challengeTitle && startsOn && endsOn) &&
		(!isCustomMode || (activityId && mechanic && unitId));

	if (!hasRequiredFields) return null;

	if (isCustomMode && activityNeedsCustomText(activityId) && !customActivityText?.trim()) {
		return null;
	}

	if (isCustomMode && unitNeedsCustomText(unitId) && !customUnitText?.trim()) {
		return null;
	}

	return (
		<div className={challengeSummaryPanelClass} aria-live="polite">
			<p className={sectionLabelClass}>Summary</p>
			<div className="space-y-2 text-sm leading-relaxed text-gray-800 dark:text-dark-text">
				<p>
					<strong className="font-semibold text-gray-900 dark:text-dark-text">{creator}</strong>
					{' is posting to your network:'}
				</p>
				<p className="text-base font-semibold text-gray-900 dark:text-dark-text">{challengeTitle}</p>
				{customDetails && <p className="text-gray-600 dark:text-gray-400">{customDetails}</p>}
				{featuredDescription && (
					<p className="text-gray-600 dark:text-gray-400">{featuredDescription}</p>
				)}
				{scheduleLine && <p>{scheduleLine}</p>}
				{descriptionText ? (
					<blockquote className="border-l-2 border-blue-300 pl-3 italic text-gray-600 dark:border-blue-600 dark:text-gray-400 whitespace-pre-wrap">
						{descriptionText}
					</blockquote>
				) : null}
				{audienceLine ? <p className={hintClass}>{audienceLine}</p> : null}
			</div>
			<p className={hintClass}>This is what they'll see on the challenge card.</p>
		</div>
	);
}
